import React, { useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip,
  TextField, InputAdornment, Chip, alpha, useTheme, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import MapIcon from '@mui/icons-material/Map';
import { useAuth } from '../context/AuthContext';

/**
 * InventoryManager Component
 * 
 * Manages the billboard inventory list with a premium UI.
 * Features:
 * - Searchable table
 * - Styled "Estado" chips
 * - Modern action buttons
 * - Gradient header
 */
export default function InventoryManager({ 
  espacios = [], 
  onOpenForm, 
  onDelete, 
  onSelect, 
  onViewMap,
  hasMore,
  loadingMore,
  onLoadMore
}) {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  // Local filtering
  const filteredEspacios = espacios.filter(s => 
    s.Codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.Provincia.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.Zona.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.Tipo_Espacio.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (estado) => {
    switch(estado) {
      case 'Disponible': return 'success';
      case 'Ocupada': return 'error';
      case 'Reservada': return 'warning';
      case 'En Mantenimiento': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 4, height: '100%', overflowY: 'auto', bgcolor: 'background.default' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="primary" sx={{ letterSpacing: '-0.03em' }}>
            Inventario de Espacios
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Gestión centralizada de activos publicitarios ({espacios.length} vallas registradas)
          </Typography>
        </Box>
        {isAdmin && (
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => onOpenForm()}
            sx={{ 
              px: 3, 
              py: 1.2, 
              borderRadius: 2.5,
              boxShadow: '0 8px 16px ' + alpha(theme.palette.primary.main, 0.25)
            }}
          >
            Nueva Valla
          </Button>
        )}
      </Box>

      {/* Table Section */}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 4, 
          overflow: 'hidden', 
          border: '1px solid', 
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
        }}
      >
        {/* Search Bar */}
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar vallas por código, provincia, zona o tipo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, bgcolor: 'background.paper' }
            }}
          />
        </Box>

        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {['Código', 'Tipo de Espacio', 'Ubicación', 'Estado', 'Tamaño', 'Acciones'].map((head) => (
                  <TableCell 
                    key={head}
                    sx={{ 
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontWeight: 700,
                      py: 2,
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEspacios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                    <Typography color="text.secondary">No se encontraron vallas que coincidan con la búsqueda.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEspacios.map((s) => (
                  <TableRow 
                    key={s.Codigo} 
                    hover 
                    sx={{ 
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: 'primary.dark' }}>{s.Codigo}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.Tipo_Espacio}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.Provincia}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.Zona}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={s.Estado} 
                        size="small" 
                        color={getStatusColor(s.Estado)}
                        sx={{ fontWeight: 700, borderRadius: 1.5, px: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{s.Tamano || 'N/A'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {isAdmin && (
                          <>
                            <Tooltip title="Editar">
                              <IconButton size="small" color="primary" onClick={() => onOpenForm(s)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" color="error" onClick={() => onDelete(s.Codigo)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Button 
                          size="small" 
                          variant="outlined"
                          startIcon={<MapIcon sx={{ fontSize: '1rem !important' }} />}
                          onClick={() => { onViewMap(); onSelect(s); }}
                          sx={{ 
                            ml: 1, 
                            borderRadius: 1.5, 
                            fontSize: '0.7rem',
                            py: 0.5
                          }}
                        >
                          Ver Mapa
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {hasMore && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              variant="text" 
              onClick={onLoadMore} 
              disabled={loadingMore}
              sx={{ fontWeight: 700 }}
              startIcon={loadingMore ? <CircularProgress size={16} /> : null}
            >
              {loadingMore ? 'Cargando más...' : 'Cargar más vallas'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
